import * as React from "react";

import LogoBadge from "@common/LogoBadge";
import Avatar from "@editor/components/common/designSystem/Avatar";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";

import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { useOverflowDetection } from "@replo/design-system/utils/useOverflowDetection";
import { PencilRuler } from "lucide-react";

type ShopSelectorHeaderProps = {
  workspaceName: string;
  endEnhancer?: React.ReactNode;
  className?: string;
  disableConditionalStyles?: boolean;
  isEmptyWorkspaces?: boolean;
  isPopoverOpen?: boolean;
  shopName?: string;
  isSidebarExpanded?: boolean;
};

const ShopSelectorHeader: React.FC<ShopSelectorHeaderProps> = ({
  workspaceName,
  shopName,
  endEnhancer,
  className,
  disableConditionalStyles,
  isSidebarExpanded,
}) => {
  const { user } = useCurrentUser();
  const [shopOverflowRef, isShopOverflowing] = useOverflowDetection();
  const [workspaceOverflowRef, isWorkspaceOverflowing] = useOverflowDetection();
  const { project } = useCurrentProjectContext();
  const isV2Project = project?.hostingType === "replo";

  if (!user) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "flex flex-row gap-2 rounded-md h-10 relative justify-center items-center",
        !disableConditionalStyles && "cursor-pointer",
        !isSidebarExpanded && "hover:bg-transparent",
        className,
      )}
    >
      <Avatar
        icon={
          isV2Project ? (
            <LogoBadge className="h-8 w-8 cursor-pointer text-black" />
          ) : (
            <PencilRuler size={16} className="text-muted" />
          )
        }
        isSquare
        size="base"
        className="py-0.5 rounded-md items-center w-8"
        imageClassName={twMerge(
          "w-8 h-8 bg-transparent",
          !isV2Project &&
            "text-primary border-0.5 border-menu-item-selected bg-sheet",
        )}
      />
      {isSidebarExpanded && (
        <>
          <div className="flex w-full min-w-0 flex-col text-left h-10">
            <div className="flex w-full items-center justify-between gap-1">
              <div className="text-default inline-grid w-full text-base font-semibold">
                {isShopOverflowing ? (
                  <Tooltip content={shopName} side="right" triggerAsChild>
                    <span ref={shopOverflowRef} className="truncate min-w-0">
                      {shopName}
                    </span>
                  </Tooltip>
                ) : (
                  <span ref={shopOverflowRef} className="truncate min-w-0">
                    {shopName}
                  </span>
                )}
              </div>
              {endEnhancer}
            </div>
            <div className="flex gap-1 items-center text-muted text-sm">
              {isWorkspaceOverflowing ? (
                <Tooltip content={workspaceName} side="right" triggerAsChild>
                  <span ref={workspaceOverflowRef} className="truncate min-w-0">
                    {workspaceName}
                  </span>
                </Tooltip>
              ) : (
                <span
                  ref={workspaceOverflowRef}
                  className={twMerge("truncate min-w-0")}
                >
                  {workspaceName}
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ShopSelectorHeader;
